/**
 * ─── Entity About Page ─────────────────────────────────────
 * Reusable "About" page for every business entity system.
 * Renders features, benefits, stats, and module links from
 * centralized entity-about-data.ts.
 *
 * Add new entities by adding an entry to entity-about-data.ts
 * and a route in router.tsx — no component changes needed.
 */

import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button } from '@repo/ui-core'
import { getAboutData } from '../lib/entity-about-data'
import type { AboutEntityData, AboutFeature } from '../lib/entity-about-data'
import {
  ArrowRight, CheckCircle2, Star, Zap, LayoutDashboard,
  // Re-export dynamic icon resolution
  Building2, Shirt, Car, UtensilsCrossed, HeartPulse, Briefcase, Droplets, Truck,
  Users, UserPlus, PiggyBank, Landmark, Calculator, Wallet, FileText, DollarSign,
  Package, Tags, ClipboardList, BarChart3, Percent,
  GraduationCap, Calendar, CalendarCheck,
  Menu, ShoppingBag,
  Stethoscope, Clock, Fingerprint, Receipt,
  Map, Gavel,
} from 'lucide-react'

// ─── Dynamic Icon Resolver ─────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Building2, Shirt, Car, UtensilsCrossed, HeartPulse, Briefcase, Droplets, Truck,
  Users, UserPlus, PiggyBank, Landmark, Calculator, Wallet, FileText, DollarSign,
  Package, Tags, ClipboardList, BarChart3, Percent,
  GraduationCap, Calendar, CalendarCheck,
  Menu, ShoppingBag,
  Stethoscope, Clock, Fingerprint, Receipt,
  Map, Gavel, ArrowRight, CheckCircle2, Star, Zap, LayoutDashboard,
}

function Icon({ name, className }: { name: string; className?: string }) {
  const Comp = ICON_MAP[name]
  if (Comp) return <Comp className={className ?? 'h-5 w-5'} />
  return <Star className={className ?? 'h-5 w-5'} />
}

// ─── Color Map ─────────────────────────────────────────────

type ColorConfig = {
  bg: string; bgLight: string; text: string; border: string
  badge: string
}

const COLOR_MAP: Record<string, ColorConfig> = {
  blue:    { bg: 'bg-blue-600',    bgLight: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    badge: 'blue' },
  indigo:  { bg: 'bg-indigo-600',  bgLight: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  badge: 'blue' },
  red:     { bg: 'bg-red-600',     bgLight: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     badge: 'red' },
  amber:   { bg: 'bg-amber-600',   bgLight: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   badge: 'yellow' },
  rose:    { bg: 'bg-rose-600',    bgLight: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200',    badge: 'red' },
  teal:    { bg: 'bg-teal-600',    bgLight: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200',    badge: 'green' },
  cyan:    { bg: 'bg-cyan-600',    bgLight: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200',    badge: 'blue' },
  emerald: { bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'green' },
}

const DEFAULT_COLOR: ColorConfig = COLOR_MAP.blue!

function getColor(key: string): ColorConfig {
  return COLOR_MAP[key] ?? DEFAULT_COLOR
}

// ─── Feature Card ──────────────────────────────────────────

function FeatureCard({ feature, color }: { feature: AboutFeature; color: string }) {
  const c = getColor(color)
  return (
    <div className={`rounded-xl border ${c.border} ${c.bgLight} p-5 transition-shadow hover:shadow-md`}>
      <div className="mb-3 flex items-center gap-3">
        <div className={`rounded-lg ${c.bg} p-2.5 text-white`}>
          <Icon name={feature.icon} className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">{feature.title}</h3>
        </div>
      </div>
      <p className="mb-3 text-sm text-gray-600">{feature.description}</p>
      <ul className="space-y-1.5">
        {feature.details.map((d: string, i: number) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
            <CheckCircle2 className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${c.text}`} />
            <span>{d}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Main About Page ───────────────────────────────────────

export function EntityAboutPage({ entityPath }: { entityPath: string }) {
  const data = getAboutData(entityPath)
  if (!data) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900">About page not found</h2>
          <p className="mt-2 text-gray-500">No about data for: {entityPath}</p>
        </div>
      </div>
    )
  }

  const c = getColor(data.heroColor)

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      {/* ─── Hero Section ────────────────────────────────── */}
      <div className={`mb-10 rounded-2xl ${c.bg} p-8 text-white sm:p-12`}>
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <div className="rounded-xl bg-white/20 p-4">
            <Icon name={data.heroIcon} className="h-10 w-10" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{data.title}</h1>
            <p className="mt-2 text-lg text-white/80">{data.tagline}</p>
          </div>
          <Badge color={c.badge === 'blue' ? 'blue' : c.badge === 'green' ? 'green' : c.badge === 'red' ? 'red' : 'yellow'}>
            Business System
          </Badge>
        </div>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/90">{data.description}</p>

        {/* Stats bar */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {data.stats.map((stat: typeof data.stats[0], i: number) => (
            <div key={i} className="rounded-lg bg-white/15 px-4 py-3 text-center backdrop-blur-sm">
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-white/70">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Features Grid ────────────────────────────────── */}
      <section className="mb-12">
        <div className="mb-6 flex items-center gap-3">
          <Zap className={`h-6 w-6 ${c.text}`} />
          <h2 className="text-2xl font-bold text-gray-900">Features & Capabilities</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.features.map((feature: AboutFeature, i: number) => (
            <FeatureCard key={i} feature={feature} color={data.heroColor} />
          ))}
        </div>
      </section>

      {/* ─── Benefits Section ─────────────────────────────── */}
      <section className="mb-12">
        <Card>
          <CardHeader title="Key Benefits" description="Why teams choose this system" />
          <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
            {data.benefits.map((benefit: string, i: number) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <Star className={`mt-0.5 h-5 w-5 flex-shrink-0 ${c.text}`} />
                <p className="text-sm text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* ─── Module Quick Links ───────────────────────────── */}
      <section>
        <div className="mb-6 flex items-center gap-3">
          <LayoutDashboard className={`h-6 w-6 ${c.text}`} />
          <h2 className="text-2xl font-bold text-gray-900">Explore Modules</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.modules.map((mod: typeof data.modules[0], i: number) => (
            <Link
              key={i}
              to={mod.path}
              className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className={`rounded-lg ${c.bgLight} p-2.5`}>
                <Icon name={mod.icon} className={`h-5 w-5 ${c.text}`} />
              </div>
              <span className="flex-1 text-sm font-medium text-gray-900 group-hover:text-gray-700">
                {mod.label}
              </span>
              <ArrowRight className="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-1 group-hover:text-gray-500" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
